import { ContentCopy } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState, type FC } from "react";
import * as yup from "yup";
import { useAlert } from "../../../contexts/AlertProvider";
import FormProvider from "../../../helpers/forms/FormProvider";
import { useSubCommunities } from "../../../queries/communities.query";
import { useManagers } from "../../../queries/users.query";
import { FormTextField } from "../../form-components/select-options/FormText";
import SelectOptions from "../../form-components/select-options/SelectOptions";
import { ProjectRequestFieldsEditor } from "./ProjectRequestFieldsEditor";

import { type Team } from "@entities";
import { subTeamRepo, teamRepo } from "@repositories";
import { type HyperTableDef } from "@shared/frontend/hyper-table";
import { projectAidRequestUrl } from "@shared/helpers/constants";
import { Roles } from "@shared/Roles";
import { remult } from "remult";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useUser } from "../../../contexts/UserProvider";
import "./TeamCard.scss";

export interface TeamData {
  id?: string;
  name: string;
  managers: string[];
  parentTeam?: string;
  subCommunities?: string[];
  adminId?: string;
  type?: string;
  projectExplanation?: string;
  volunteerDescription?: string;
  isMainOrganizationTeam?: boolean;
}

interface TeamCardProps {
  table: HyperTableDef<Team>;
  defaultTeamData?: TeamData;
  isAdd?: boolean;
  setIsAdd?: React.Dispatch<React.SetStateAction<boolean>>;
}

const validationSchema = (
  subCommunities: Team[],
  editedCommunityId: string | undefined,
) =>
  yup.object().shape({
    name: yup
      .string()
      .required("שדה חובה")
      .max(35, "השם צריך להכיל עד 35 תווים")
      .test(
        "unique-name",
        "קיים כבר פרויקט בשם זה, יש לבחור שם אחר",
        (value) => {
          if (!value || editedCommunityId) return true;
          return !subCommunities?.some((community) => community.name === value);
        },
      ),
    managers: yup.array().of(yup.string()).min(1, "שדה חובה"),
  });

const TeamCard: FC<TeamCardProps> = ({
  defaultTeamData,
  isAdd,
  setIsAdd,
  table,
}) => {
  const [data, setData] = useState<TeamData>();
  const { managers } = useManagers();
  const { currentUser } = useUser();
  const { currOrganization } = useOrganization();
  const { subCommunities } = useSubCommunities();
  const { setAlertText, setSeverity } = useAlert();

  useEffect(() => {
    setData(defaultTeamData);
  }, [defaultTeamData]);

  async function handleSubmit({
    name,
    managers,
    parentTeam,
    projectExplanation,
    volunteerDescription,
  }: TeamData) {
    if (!currOrganization) return;
    const projectTexts = {
      projectExplanation: (projectExplanation ?? "").trim(),
      volunteerDescription: (volunteerDescription ?? "").trim(),
    };
    if (isAdd) {
      // Creating the team
      const newTeam = await teamRepo.insert({
        name,
        organization: currOrganization,
        ...projectTexts,
      });

      // Adding relations of the team managers
      await teamRepo.relations(newTeam).managers.insert(
        managers.map((m) => ({
          managerId: m,
        })),
      );

      if (parentTeam) {
        await subTeamRepo.insert({
          teamId: parentTeam,
          subTeamId: newTeam.id,
        });
      }
    } else if (data?.id) {
      const newTeam = await teamRepo.update(data.id, {
        name,
        ...projectTexts,
      });

      // Deleting all current team managers
      await teamRepo.relations(newTeam).managers.deleteMany({
        where: {
          teamId: newTeam.id,
        },
      });

      // Inserting the new managers
      await teamRepo
        .relations(newTeam)
        .managers.insert(managers.map((m) => ({ managerId: m })));

      const isSubTeam = await subTeamRepo.count({ subTeamId: newTeam.id });

      if (parentTeam) {
        if (isSubTeam > 0) {
          // Replacing the current parent team with the new parent team
          const newParents = await subTeamRepo.updateMany({
            where: {
              subTeamId: newTeam.id,
            },
            set: {
              teamId: parentTeam,
            },
          });
          if (newParents === 0) {
            // If no parent team was found, create a new relation
            await subTeamRepo.insert({
              teamId: parentTeam,
              subTeamId: newTeam.id,
            });
          }
        } else {
          await subTeamRepo.insert({
            teamId: parentTeam,
            subTeamId: newTeam.id,
          });
        }
      } else {
        await subTeamRepo.deleteMany({
          where: {
            subTeamId: newTeam.id,
          },
        });
      }
    }
    table?.context?.current?.refetch("remove");
    setAlertText("הפעולה בוצעה בהצלחה");
    setSeverity("success");
    if (setIsAdd) setIsAdd(false);
    setData(undefined);
  }

  const options = subCommunities
    .filter(
      (sub) => sub.id !== data?.id && !data?.subCommunities?.includes(sub.id),
    )
    .map((com) => ({ content: com.name, value: com.id }));

  const aidRequestUrl =
    data?.id && !data.isMainOrganizationTeam
      ? projectAidRequestUrl(data.id)
      : "";

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "1rem" } }}
      open={!!data || !!isAdd}
      onClose={() => {
        setData(undefined);
        if (setIsAdd) setIsAdd(false);
      }}
    >
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "2vh" }}
      >
        <Typography
          fontSize="x-large"
          fontWeight={600}
          textAlign="center"
          color="secondary"
        >
          {isAdd ? "הוספת פרויקט" : "עריכת פרויקט"}
        </Typography>
        <FormProvider
          enableReinitialize
          validationSchema={validationSchema(subCommunities, data?.id)}
          className="team-card-form"
          onSubmit={handleSubmit}
          initialValues={
            data
              ? {
                  ...data,
                  projectExplanation: data.projectExplanation ?? "",
                  volunteerDescription: data.volunteerDescription ?? "",
                }
              : {
                  name: "",
                  managers: [currentUser!.id],
                  projectExplanation: "",
                  volunteerDescription: "",
                }
          }
        >
          <FormTextField
            inputProps={{ maxLength: 35 }}
            color="secondary"
            label="שם הפרויקט"
            name="name"
          />
          {remult.isAllowed(Roles.Admins) && (
            <SelectOptions
              multiple
              withElse={false}
              label="מנהלי הפרויקט"
              name="managers"
              options={managers.map((manager) => ({
                content: manager.name,
                value: manager.id,
              }))}
            />
          )}
          {data?.type !== "community" && options.length > 0 && (
            <SelectOptions
              withElse={false}
              label="פרויקט אב"
              name="parentTeam"
              options={options}
            />
          )}
          <Stack spacing={2}>
            <FormTextField
              color="secondary"
              label="טקסט לבקשות סיוע"
              name="projectExplanation"
              multiline
              minRows={3}
              withInfo="הטקסט שיוצג למבקשי סיוע כשהם נכנסים לקישור של הפרויקט"
            />
            <FormTextField
              color="secondary"
              label="טקסט למתנדבים בפיד"
              name="volunteerDescription"
              multiline
              minRows={3}
              withInfo="הטקסט שיוצג למתנדבים כשהם נכנסים לפיד של הפרויקט"
            />
            {aidRequestUrl && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextField
                  color="secondary"
                  label="קישור למבקשי סיוע"
                  value={aidRequestUrl}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                />
                <IconButton
                  type="button"
                  aria-label="העתק קישור למבקשי סיוע"
                  onClick={async () => {
                    await navigator.clipboard.writeText(aidRequestUrl);
                    setAlertText("הקישור הועתק ללוח");
                    setSeverity("success");
                  }}
                >
                  <ContentCopy />
                </IconButton>
              </Stack>
            )}
            {data?.id && !data.isMainOrganizationTeam && (
              <ProjectRequestFieldsEditor teamId={data.id} />
            )}
          </Stack>
          <Button type="submit" variant="contained" color="secondary">
            שמירה
          </Button>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default TeamCard;
